import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsClockwise, FilePdf, Eye, Globe, CheckCircle, WarningCircle, CircleNotch, X, Sparkle } from "@phosphor-icons/react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { HpiReportView } from "@/components/HpiReportView";

const errMsg = (e, f) => formatApiErrorDetail(e?.response?.data?.detail) || f;

function Preview({ month, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api.get(`/admin/hpi/reports/${month}`).then((x) => { setR(x.data); setText(x.data.summary_text || ""); }); }, [month]);
  const save = async () => {
    try { await api.put(`/admin/hpi/reports/${month}/summary`, { summary_text: text }); setR({ ...r, summary_text: text }); toast.success("Summary saved"); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto p-4" onClick={onClose}>
      <div data-testid="hpi-preview-dialog" className="my-6 w-full max-w-5xl rounded-3xl bg-slate-50 p-6 md:p-10" onClick={(e) => e.stopPropagation()}>
        <button data-testid="hpi-preview-close" onClick={onClose} className="float-right text-slate-500 hover:text-slate-900"><X size={22} /></button>
        {!r ? <Loading /> : (
          <>
            {r.status === "draft" && (
              <div className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">Draft — edit the published summary</div>
                <textarea data-testid="hpi-summary-edit" value={text} onChange={(e) => setText(e.target.value)} rows={4}
                  className="w-full rounded-xl border border-amber-200 bg-white p-3 text-sm outline-none focus:border-riq-primary" />
                <button data-testid="hpi-summary-save" onClick={save} className="mt-2 rounded-full bg-neutral-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-riq-primary transition-colors">Save summary</button>
              </div>
            )}
            <HpiReportView report={r} />
          </>
        )}
      </div>
    </div>
  );
}

export const AdminHpiReports = () => {
  const [cfg, setCfg] = useState(null);
  const [reports, setReports] = useState([]);
  const [base, setBase] = useState("");
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => Promise.all([
    api.get("/admin/hpi/index/config").then((r) => { setCfg(r.data); setBase(r.data.base_month || r.data.suggested_base || ""); setMonth((m) => m || r.data.coverage.at(-1)?.month || ""); }),
    api.get("/admin/hpi/reports").then((r) => setReports(r.data.reports)),
  ]).catch(() => setCfg(false)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(errMsg(e, "Action failed")); } finally { setBusy(""); }
  };

  if (cfg === false) return <Card>Could not load index settings.</Card>;
  if (!cfg) return <Loading />;
  const cpi = cfg.cpi;
  const spin = (k) => busy === k && <CircleNotch size={14} className="animate-spin" />;

  return (
    <div className="space-y-6" data-testid="admin-hpi-reports">
      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="hpi-base-card">
          <div className="text-sm font-semibold">Base month (index = 100)</div>
          <p className="text-xs text-muted-foreground mt-1">Suggested: <b>{cfg.suggested_base || "—"}</b> (first month with ≥10% of the basket priced, else the best-covered month).</p>
          <div className="mt-3 flex items-center gap-2">
            <select data-testid="hpi-base-select" value={base} onChange={(e) => setBase(e.target.value)} className="rounded-full border border-riq-border bg-white px-3 py-2 text-sm">
              {cfg.coverage.map((c) => <option key={c.month} value={c.month}>{c.month} · {Math.round(c.coverage * 100)}% priced</option>)}
            </select>
            <button data-testid="hpi-base-confirm" disabled={!base || busy} onClick={() => act("base", () => api.put("/admin/hpi/index/base", { base_month: base }), `Base month set to ${base}`)}
              className="flex items-center gap-1.5 rounded-full bg-riq-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{spin("base")} Confirm</button>
          </div>
          <div data-testid="hpi-base-status" className={`mt-2 flex items-center gap-1.5 text-xs ${cfg.base_month ? "text-emerald-700" : "text-amber-700"}`}>
            {cfg.base_month ? <CheckCircle size={14} weight="fill" /> : <WarningCircle size={14} weight="fill" />}
            {cfg.base_month ? `Confirmed ${cfg.base_month} by ${cfg.confirmed_by}` : "Not confirmed yet — reports use the suggested month"}
          </div>
        </Card>
        <Card data-testid="hpi-cpi-card">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Official CPI (Stats SA P0141)</div>
            <button data-testid="hpi-cpi-refresh" disabled={!!busy} onClick={() => act("cpi", () => api.post("/admin/hpi/cpi/refresh"), "CPI refreshed from Stats SA")}
              className="flex items-center gap-1.5 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary disabled:opacity-60">{spin("cpi") || <ArrowsClockwise size={14} />} Fetch latest</button>
          </div>
          <div data-testid="hpi-cpi-status" className="mt-3 text-sm">
            {cpi?.latest_month ? <>Latest CPI month <b>{cpi.latest_month}</b> · release {cpi.release} · {cpi.values} values</> : <span className="text-amber-700">Not fetched yet</span>}
            {cpi?.error && <div className="mt-1 text-xs text-red-600 break-all">{cpi.error}</div>}
            <div className="text-xs text-muted-foreground mt-1">Fetched automatically on the 22nd each month{cpi?.fetched_at ? ` · last ${cpi.fetched_at.slice(0, 16).replace("T", " ")}` : ""}.</div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold mr-auto">Monthly reports</div>
          <select data-testid="hpi-report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-full border border-riq-border bg-white px-3 py-2 text-sm">
            {cfg.coverage.map((c) => <option key={c.month} value={c.month}>{c.month}</option>)}
          </select>
          <button data-testid="hpi-generate-draft" disabled={!month || !!busy} onClick={() => act("gen", () => api.post("/admin/hpi/reports", { month }), `Draft generated for ${month}`)}
            className="flex items-center gap-1.5 rounded-full bg-riq-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60">{spin("gen") || <Sparkle size={14} weight="fill" />} Generate draft</button>
        </div>
        <div className="mt-4 divide-y divide-riq-border rounded-2xl border border-riq-border">
          {!reports.length && <div className="p-6 text-center text-sm text-muted-foreground">No reports yet.</div>}
          {reports.map((r) => (
            <div key={r.month} data-testid={`hpi-report-row-${r.month}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{r.month} <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{r.status}</span></div>
                <div className="text-xs text-muted-foreground">HPI {r.data?.headline?.index?.toFixed(1) ?? "—"} · m/m {r.data?.headline?.mom ?? "—"}% · updated {r.updated_at?.slice(0, 10)}{r.published_at ? ` · published ${r.published_at.slice(0, 10)}` : ""}</div>
              </div>
              <button data-testid={`hpi-report-preview-${r.month}`} onClick={() => setPreview(r.month)} className="flex items-center gap-1 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary"><Eye size={14} /> Preview</button>
              <button data-testid={`hpi-report-pdf-${r.month}`} onClick={() => downloadFile(`/admin/hpi/reports/${r.month}/pdf`, `sliply-hpi-${r.month}.pdf`).catch(() => toast.error("PDF failed"))} className="flex items-center gap-1 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary"><FilePdf size={14} /> PDF</button>
              {r.status === "published" ? (
                <>
                  <a data-testid={`hpi-report-public-${r.month}`} href={`/hpi/${r.month}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-full border border-riq-border px-3 py-1.5 text-xs font-medium hover:border-riq-primary"><Globe size={14} /> View live</a>
                  <button data-testid={`hpi-report-unpublish-${r.month}`} onClick={() => act(`u${r.month}`, () => api.post(`/admin/hpi/reports/${r.month}/unpublish`), "Unpublished")} className="rounded-full px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50">Unpublish</button>
                </>
              ) : (
                <button data-testid={`hpi-report-publish-${r.month}`} onClick={() => window.confirm(`Publish the ${r.month} report to sliply.co.za/hpi?`) && act(`p${r.month}`, () => api.post(`/admin/hpi/reports/${r.month}/publish`), "Published to sliply.co.za/hpi")}
                  className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Publish</button>
              )}
            </div>
          ))}
        </div>
      </Card>
      {preview && <Preview month={preview} onClose={() => setPreview(null)} onChanged={load} />}
    </div>
  );
};
